import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

const ServicesPage = () => {
  const [user, setUser] = useState(null);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(null));
    api.get("/business/categories").then(r => setCategories(r.data?.categories || [])).catch(() => {});
  }, []);

  // Ref so `search` keystrokes don't recreate fetchServices (which would
  // otherwise refetch on every letter). Search value read via ref;
  // effect only refetches on category change; form submit still works.
  const searchRef = useRef(search);
  useEffect(() => { searchRef.current = search; }, [search]);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeCat) params.category = activeCat;
      const s = searchRef.current;
      if (s && s.trim()) params.q = s.trim();
      const r = await api.get("/services", { params });
      setServices(r.data?.services || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load services");
    } finally {
      setLoading(false);
    }
  }, [activeCat]);
  useEffect(() => { fetchServices(); }, [fetchServices]);

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="services-page">
      <SharedNavigation currentUser={user} activePage="business" />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
              <span>🛠</span> Services Directory
            </h1>
            <p className="text-gray-400 mt-1 text-sm">
              Music services, design, branding, marketing, tax & legal — all from RGM creators.
            </p>
          </div>
          <Link to="/business">
            <Button variant="outline" className="text-white border-purple-500/50">
              Browse Businesses
            </Button>
          </Link>
        </div>

        {/* Search + filters */}
        <form onSubmit={(e) => { e.preventDefault(); fetchServices(); }} className="mb-4 flex gap-2 flex-wrap"
              data-testid="services-search-form">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services…"
            className="bg-black/30 text-white border-gray-700 max-w-sm"
            data-testid="service-search-input"
          />
          <Button type="submit" className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="service-search-submit">
            Search
          </Button>
        </form>
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1" data-testid="service-category-filters">
          <button onClick={() => setActiveCat("")} className={`text-xs px-3 py-1 rounded-full border whitespace-nowrap ${!activeCat ? "bg-purple-600/40 border-purple-400 text-white" : "bg-black/30 border-gray-700 text-gray-300"}`} data-testid="svc-cat-all">All</button>
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => setActiveCat(c.key)}
              className={`text-xs px-3 py-1 rounded-full border whitespace-nowrap ${activeCat === c.key ? "bg-purple-600/40 border-purple-400 text-white" : "bg-black/30 border-gray-700 text-gray-300"}`}
              data-testid={`svc-cat-${c.key}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="services-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-12" data-testid="services-empty">
            <p className="text-5xl mb-3">🛠</p>
            <p className="text-white text-lg font-semibold">No services yet</p>
            <p className="text-gray-400 text-sm mt-1">Be the first — list your business and add services.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="services-grid">
            {services.map((row) => {
              const s = row.service || {};
              return (
                <Link
                  to={`/business/${row.business_id}`}
                  key={s.service_id}
                  className="block"
                  data-testid={`svc-card-${s.service_id}`}
                >
                  <Card className="p-4 bg-gray-900/40 border-gray-800 hover:border-purple-500/40 transition-colors h-full">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold truncate">{s.title}</h3>
                        <p className="text-purple-300 text-[11px] uppercase tracking-wider mt-0.5">{(s.category || row.business_category || "other").replace("_", " ")}</p>
                        {s.description && <p className="text-sm text-gray-400 mt-2 line-clamp-2">{s.description}</p>}
                      </div>
                      {s.price_label && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 whitespace-nowrap shrink-0">
                          {s.price_label}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs text-gray-500 border-t border-gray-800 pt-2">
                      <img
                        src={getProfilePhotoUrl(row.owner || {})}
                        alt=""
                        className="w-5 h-5 rounded-full object-cover"
                      />
                      <span className="truncate">{row.owner?.artist_name}</span>
                      <span className="text-gray-700">·</span>
                      <span className="truncate">{row.business_name}</span>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesPage;
